import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import TenantCounter from '../TenantCounter';
import ArrowDown from '@spa/shared/svg/arrowDown';
import ArrowNext from '@spa/shared/svg/arrow-next';
import { formatDate, formatMoney } from '@spa/shared/utils';
import {
  Content,
  Accordion,
  Header,
  Title,
  Subtitle,
  Instalments,
  Instalment,
  FixedDatesTitle,
  WrapperMobile,
  MainHeaderMobile,
  WrapperDesktop,
  MainHeaderDesktop
} from './styles';
import StatusBanner from '@spa/shared/components/Unisystem/StatusBanner';
import { InfoIcon } from '@spa/shared/svg';
import { MediaContext } from '@spa/shared/contexts/media';

const FixedContracts = ({ guests, maxGuests, setGuests, options, selectedOption, selectOption, classNames = {} }) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const { is } = useContext(MediaContext);

  const className = Object.keys(classNames)
    .filter(name => classNames[name])
    .join(' ');

  const [Wrapper, MainHeader] = useMemo(
    () => (is.mobile ? [WrapperMobile, MainHeaderMobile] : [WrapperDesktop, MainHeaderDesktop]),
    [is.mobile]
  );

  return (
    <Wrapper>
      <MainHeader>
        <FixedDatesTitle>{t('Fixed dates')}</FixedDatesTitle>
        <TenantCounter guests={guests} maxGuests={maxGuests} setGuests={setGuests} />
      </MainHeader>
      <StatusBanner
        color={'yellow'}
        icon={<InfoIcon />}
        message={'This landlord only works with fixed dates. Please choose the ones that work best for you'}
      />
      <Content>
        {options.map(
          ({ id: optionId, start_date: startDate, end_date: endDate, instalments, contract_value: contractValue }) => {
            const isSelected = optionId === selectedOption;

            const lowerInstalment = instalments?.reduce(
              (acc, instalment) =>
                (!acc && instalment.value.amount) || instalment.value.amount < acc
                  ? formatMoney(instalment.value.amount, instalment.value.currency_code)
                  : acc,
              null
            );

            return (
              <Accordion
                key={optionId}
                className={`${className} ${isSelected ? ' selected' : ''}`}
                onClick={() => selectOption(isSelected ? null : optionId)}
              >
                <Header>
                  <Title>
                    {formatDate(new Date(startDate), language, 'd MMMM yyyy')}
                    <ArrowNext />
                    {formatDate(new Date(endDate), language, 'd MMMM yyyy')}
                  </Title>
                  {instalments?.length > 1 ? (
                    <Subtitle isSelected={isSelected}>
                      {instalments?.length} {t('Instalments from')} {lowerInstalment}
                      <ArrowDown />
                    </Subtitle>
                  ) : (
                    <Subtitle isSelected={isSelected}>
                      {`1 ${t('Instalment')}`} - {formatMoney(contractValue.amount, contractValue.currency_code)}
                    </Subtitle>
                  )}
                </Header>
                {isSelected && instalments && (
                  <Instalments>
                    {instalments?.map((instalment, index) => (
                      <Instalment key={index}>
                        <span>{formatDate(new Date(instalment.date), language, 'd MMMM yyyy')}</span>
                        <span>{formatMoney(instalment.value.amount, instalment.value.currency_code)}</span>
                      </Instalment>
                    ))}
                  </Instalments>
                )}
              </Accordion>
            );
          }
        )}
      </Content>
    </Wrapper>
  );
};

FixedContracts.propTypes = {
  guests: PropTypes.number.isRequired,
  maxGuests: PropTypes.number.isRequired,
  setGuests: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  selectedOption: PropTypes.number,
  selectOption: PropTypes.func.isRequired,
  classNames: PropTypes.object
};

export default FixedContracts;
